import { Row } from '@tanstack/react-table';
import {
  Button,
  Container,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  RiAccountCircleLine,
  RiDeleteBinLine,
  RiMore2Line,
  RiPencilFill,
  Stack,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import { TextInput } from 'component-library/TextInput/TextInput';
import i18next from 'i18next';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PlotGroupSuccessResponse } from '@/api/rest/resources/types/organization';
import { WidgetSlotConfiguration } from '@/api/rest/resources/types/widget';
import { useSetPlotGroupFilters } from '@/components/PlotGroups/hooks/useSetPlotGroup';
import { parsePlotGroupBodyData } from '@/components/PlotGroups/utils/parsePlotGroupBodyData';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { getPlotGroupFilter, usePlotGroupFiltersPills } from '@/components/widgets/config/PlotGroupFilters';
import { GenericTable } from '@/components/widgets/o/GenericTable/GenericTable';
import { SortButton } from '@/components/widgets/o/GenericTable/SortButton';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useDebounce } from '@/hooks/useDebounce';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePathSet } from '@/hooks/usePathSet';
import { useUserMe } from '@/hooks/useUserMe';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { EditGroup } from '@/pages/shared/components/plot-group/EditPlotGroup';
import { useLimitedAccessOrganization } from '@/pages/shared/hooks/useLimitedAccessOrganization';
import { isNullish } from '@/utils/booleans';
import { squareMetersToHectares } from '@/utils/plot';
import { MetadataColumn } from '@/utils/table/mergeColumns';

import { CreateLimitedAccessOrganization } from '../components/plot-group/CreateLimitedAccessOrganization';
import { EditLimitedAccessOrganization } from '../components/plot-group/EditLimitedAccessOrganization';
import { useWorkstreamId } from '../home/hooks/useWorkstreamId';
import { useIsLimitedAccessUser } from '../hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '../hooks/useIsOrganizationMember';
import { useOrganizationId } from '../hooks/useOrganizationId';
import { useOrganizationPlotGroupEIIStats, useOrganizationPlotGroups } from '../hooks/useOrganizationPlotGroup';
import { ProjectTypeAccessorKey } from '../projects/pages/project/pages/sustainableProduction/components/plot-list/constants';
import { DeleteGroupWithConfirmation } from './components/DeleteGroupWithConfirmation';

const ActionsCell = ({
  row,
  confirmGroupEdit,
  confirmGroupDelete,
  confirmGroupAccess,
}: {
  row: Row<PlotGroupSuccessResponse>;
  confirmGroupEdit: (group: PlotGroupSuccessResponse) => void;
  confirmGroupDelete: (group: PlotGroupSuccessResponse) => void;
  confirmGroupAccess: (group: PlotGroupSuccessResponse) => void;
}) => {
  const group = row.original;
  const { t } = useTranslation();

  return (
    <Dropdown modal={true}>
      <DropdownTrigger data-testid='plot-row-overflow-menu'>
        <div className='w-10 shrink-0 rounded-full border border-primary-100 p-2 text-primary-100 hover:bg-neutral-black-4'>
          <RiMore2Line size={24} />
        </div>
      </DropdownTrigger>

      <DropdownContent side='bottom' sideOffset={10}>
        <DropdownItem onClick={() => confirmGroupEdit(group)} leftAdornment={<RiPencilFill />}>
          {t('global.organization.plotGroup.editGroup.title')}
        </DropdownItem>
        <DropdownItem onClick={() => confirmGroupAccess(group)} leftAdornment={<RiAccountCircleLine />}>
          {t('global.organization.limitedAccessOrganization.createLimitedAccessOrganization.buttonLabel')}
        </DropdownItem>
        <DropdownItem onClick={() => confirmGroupDelete(group)} leftAdornment={<RiDeleteBinLine />}>
          {t('global.organization.plotGroup.deleteGroup.title')}
        </DropdownItem>
      </DropdownContent>
    </Dropdown>
  );
};

const NameCell = ({ row }: { row: Row<PlotGroupSuccessResponse> }) => {
  const plotGroup = row.original;
  const { name } = plotGroup;
  return (
    <span className='flex items-center gap-2'>
      <span className='bg-neutral-hover w-12 h-12 rounded-md items-center flex justify-center flex-shrink-0'>
        <Icon icon='ri-gallery-view-2' className='w-4 p-0 m-0 text-primary-100' />
      </span>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='truncate max-w-[180px]'>{name}</span>
        </TooltipTrigger>
        <TooltipContent side='top' align='start' key={`plotgroup-name-${plotGroup.id}`}>
          <span className='typography-1'>{name}</span>
        </TooltipContent>
      </Tooltip>
    </span>
  );
};

const FIXED_FILTERS_HEIGHT_PX = 36;

const FiltersCell = ({ row }: { row: Row<PlotGroupSuccessResponse> }) => {
  const { t } = useTranslation();

  const plotGroup = row.original;

  const additionalPlotIds = plotGroup.additional_plot_ids ?? [];
  const hasAdditionalPlotsPill = additionalPlotIds.length > 0;

  const parsedPlotGroup = parsePlotGroupBodyData(plotGroup);
  const allFilters = getPlotGroupFilter(parsedPlotGroup);
  const totalPillsCount = (allFilters?.length ?? 0) + additionalPlotIds.length;

  const [visibleCount, setVisibleCount] = useState(allFilters?.length ?? 0);

  const visibleFiltersRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = visibleFiltersRef.current;
    if (!container || visibleCount <= 0) {
      return;
    }

    const hasOverflow = container.scrollHeight > container.clientHeight;
    if (hasOverflow) {
      setVisibleCount((previous) => Math.max(0, previous - 1));
    }
  }, [visibleCount, totalPillsCount]);

  const renderedPillsCount = visibleCount + (hasAdditionalPlotsPill ? 1 : 0);
  const extraFilters = Math.max(0, totalPillsCount - renderedPillsCount);

  const visiblePlotGroupFilters: WidgetSlotConfiguration = usePlotGroupFiltersPills({
    plotGroup: parsedPlotGroup,
    filtersArg: allFilters.slice(0, visibleCount),
    canEditFilters: false,
    styles: {
      container: 'gap-0 flex-wrap p-0',
    },
  });

  const allPlotGroupFilters: WidgetSlotConfiguration = usePlotGroupFiltersPills({
    plotGroup: parsedPlotGroup,
    filtersArg: allFilters,
    canEditFilters: false,
    styles: {
      container: 'gap-0 flex-wrap p-0',
    },
  });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Stack className='flex gap-1 py-0 justify-center'>
          <div
            ref={visibleFiltersRef}
            className='max-w-xs overflow-hidden'
            style={{ height: `${FIXED_FILTERS_HEIGHT_PX}px` }}
          >
            <Stack key={`plotgroup-filters-${plotGroup.id}`} className='flex flex-row justify-start items-start'>
              <WidgetSlotRenderer key={`plotgroup-filters-${plotGroup.id}`} nestedWidget={visiblePlotGroupFilters} />
            </Stack>
          </div>
          {extraFilters > 0 && (
            <span className='items-center w-fit bg-bg-light-grey typography-body2 rounded-lg text-text-secondary shrink-0 whitespace-nowrap px-1 py-1'>
              {t('global.widget.filters.sections.extraAdditionalFilters', { count: extraFilters })}
            </span>
          )}
        </Stack>
      </TooltipTrigger>
      <TooltipContent side='top' align='start' key={`plotgroup-filters-tooltip-${plotGroup.id}`}>
        <Stack key={`plotgroup-filters-tooltip-${plotGroup.id}`} className='gap-2'>
          <WidgetSlotRenderer key={`plotgroup-filters-tooltip-${plotGroup.id}`} nestedWidget={allPlotGroupFilters} />
        </Stack>
      </TooltipContent>
    </Tooltip>
  );
};

const PlotGroupsTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pathSet = usePathSet();

  const [groupForAccess, setGroupForAccess] = useState<PlotGroupSuccessResponse | null>(null);
  const [groupForEdit, setGroupForEdit] = useState<PlotGroupSuccessResponse | null>(null);
  const [groupForDeletion, setGroupForDeletion] = useState<PlotGroupSuccessResponse | null>(null);

  const organizationId = useOrganizationId();
  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();
  const plotGroups = useOrganizationPlotGroups(organizationId).data;
  const eiiStats = useOrganizationPlotGroupEIIStats(organizationId).data;

  const user = useUserMe().data;
  const limitedAccessOrganizations = useLimitedAccessOrganization(organizationId, { user_id: user.id }).data;
  const visiblePlotGroups =
    limitedAccessOrganizations && limitedAccessOrganizations.length > 0
      ? limitedAccessOrganizations.map((lao) => lao.plot_group)
      : plotGroups;
  const hideActions = isLimitedAccessUser || !isOrganizationMember;

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  const filteredPlotGroups = useMemo(() => {
    if (isNullish(debouncedSearchQuery.trim())) return visiblePlotGroups;

    const queryString = debouncedSearchQuery.toLowerCase();

    return visiblePlotGroups.filter((group) => group.name?.toLowerCase().includes(queryString));
  }, [visiblePlotGroups, debouncedSearchQuery]);

  const confirmGroupDelete = (group: PlotGroupSuccessResponse) => {
    setGroupForDeletion(group);
  };

  const confirmGroupEdit = (group: PlotGroupSuccessResponse) => {
    setGroupForEdit(group);
  };

  const confirmGroupAccess = (group: PlotGroupSuccessResponse) => {
    setGroupForAccess(group);
  };

  const defaultColumns = useMemo<MetadataColumn<PlotGroupSuccessResponse>[]>(() => {
    return [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <SortButton column={column} className='-ml-1.5 px-0'>
            {t(`shared.plots.tableHeader.name`)}
          </SortButton>
        ),
        cell: ({ row }) => <NameCell row={row} />,
      },
      {
        accessorKey: ProjectTypeAccessorKey.labels,
        header: ({ column }) => (
          <SortButton column={column} className='-ml-1.5 px-0'>
            {t(`global.widget.filters.button.filters`)}
          </SortButton>
        ),
        meta: {
          styles: {
            cell: 'py-2',
          },
        },
        cell: ({ row }) => <FiltersCell row={row} />,
      },
      {
        accessorKey: 'total_plots',
        header: ({ column }) => (
          <span className='flex items-center'>
            <SortButton column={column} className='-ml-1.5 px-0'>
              {t(`global.widget.totalAssets`)}
            </SortButton>
            <Tooltip>
              <TooltipTrigger>
                <Icon icon='ri-information-line' className='ml-1 text-active-54 typography-body1' />
              </TooltipTrigger>
              <TooltipContent side='top'>
                {t(`global.widget.totalAssetsPlotGroupTooltip`)}
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </span>
        ),
        cell: ({ getValue }) => <span>{getValue() as string}</span>,
      },
      {
        accessorKey: 'total_area',
        header: ({ column }) => (
          <span className='flex items-center'>
            <SortButton column={column} className='-ml-1.5 px-0'>
              {t(`global.widget.totalSize`)}
            </SortButton>
            <Tooltip>
              <TooltipTrigger>
                <Icon icon='ri-information-line' className='ml-1 text-active-54 typography-body1' />
              </TooltipTrigger>
              <TooltipContent side='top'>
                {t(`global.widget.totalSizePlotGroupTooltip`)}
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </span>
        ),
        cell: ({ getValue }) => (
          <span>{`${getDisplayNumber(squareMetersToHectares((getValue() as number) ?? 0), i18next.language) as string} ha`}</span>
        ),
      },
      {
        accessorKey: 'ecological_integrity_score',
        accessorFn: (row) => eiiStats?.[row.id]?.ecological_integrity_score,
        header: ({ column }) => (
          <span className='flex items-center'>
            <SortButton column={column} className='-ml-1.5 px-0'>
              {t(`shared.ncaDetail.details.ecosystemIntegrityIndex.abbreviations.ecosystemIntegrityIndex`)}
            </SortButton>
            <Tooltip>
              <TooltipTrigger>
                <Icon icon='ri-information-line' className='ml-1 text-active-54 typography-body1' />
              </TooltipTrigger>
              <TooltipContent side='top'>
                {t(`global.widget.ecologicalIntegrityIndexTooltip`)}
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </span>
        ),
        cell: ({ getValue }) => {
          const val = getValue() as number | null | undefined;
          return <span>{!isNullish(val) ? (getDisplayNumber(val, i18next.language) as string) : '-'}</span>;
        },
      },
      {
        accessorKey: 'functional_integrity_score',
        accessorFn: (row) => eiiStats?.[row.id]?.functional_integrity_score,
        header: ({ column }) => (
          <span className='flex items-center'>
            <SortButton column={column} className='-ml-1.5 px-0'>
              {t(`shared.ncaDetail.details.ecosystemIntegrityIndex.abbreviations.functionalIntegrity`)}
            </SortButton>
            <Tooltip>
              <TooltipTrigger>
                <Icon icon='ri-information-line' className='ml-1 text-active-54 typography-body1' />
              </TooltipTrigger>
              <TooltipContent side='top'>
                {t(`global.widget.ecologicalIntegrityIndex.decomposition.functional_integrity_score.tooltip.body`)}
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </span>
        ),
        cell: ({ getValue }) => {
          const val = getValue() as number | null | undefined;
          return <span>{!isNullish(val) ? (getDisplayNumber(val, i18next.language) as string) : '-'}</span>;
        },
      },
      {
        accessorKey: 'compositional_integrity_score',
        accessorFn: (row) => eiiStats?.[row.id]?.compositional_integrity_score,
        header: ({ column }) => (
          <span className='flex items-center'>
            <SortButton column={column} className='-ml-1.5 px-0'>
              {t(`shared.ncaDetail.details.ecosystemIntegrityIndex.abbreviations.compositionalIntegrity`)}
            </SortButton>
            <Tooltip>
              <TooltipTrigger>
                <Icon icon='ri-information-line' className='ml-1 text-active-54 typography-body1' />
              </TooltipTrigger>
              <TooltipContent sideOffset={3} side='top'>
                {t(`global.widget.ecologicalIntegrityIndex.decomposition.compositional_integrity_score.tooltip.body`)}
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </span>
        ),
        cell: ({ getValue }) => {
          const val = getValue() as number | null | undefined;
          return <span>{!isNullish(val) ? (getDisplayNumber(val, i18next.language) as string) : '-'}</span>;
        },
      },
      {
        accessorKey: 'structural_integrity_score',
        accessorFn: (row) => eiiStats?.[row.id]?.structural_integrity_score,
        header: ({ column }) => (
          <span className='flex items-center'>
            <SortButton column={column} className='-ml-1.5 px-0'>
              {t(`shared.ncaDetail.details.ecosystemIntegrityIndex.abbreviations.structuralIntegrity`)}
            </SortButton>
            <Tooltip>
              <TooltipTrigger>
                <Icon icon='ri-information-line' className='ml-1 text-active-54 typography-body1' />
              </TooltipTrigger>
              <TooltipContent side='top'>
                {t(`global.widget.ecologicalIntegrityIndex.decomposition.structural_integrity_score.tooltip.body`)}
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </span>
        ),
        cell: ({ getValue }) => {
          const val = getValue() as number | null | undefined;
          return <span>{!isNullish(val) ? (getDisplayNumber(val, i18next.language) as string) : '-'}</span>;
        },
      },
      {
        accessorKey: 'actions',
        header: () => null,
        cell: ({ row }) => {
          if (hideActions) {
            return null;
          }

          return (
            <span className='self-end'>
              <ActionsCell
                row={row}
                confirmGroupDelete={confirmGroupDelete}
                confirmGroupEdit={confirmGroupEdit}
                confirmGroupAccess={confirmGroupAccess}
              />
            </span>
          );
        },
      },
    ];
  }, [t, hideActions, eiiStats]);

  const { setPlotGroupFilters } = useSetPlotGroupFilters();

  const workstreamId = useWorkstreamId();

  const onRowClick = async (row: Row<PlotGroupSuccessResponse>) => {
    const group = row.original;
    const plotGroup = parsePlotGroupBodyData(group);
    await setPlotGroupFilters({
      filters: plotGroup.filters,
      groupBy: plotGroup.groupBy,
      additional_plot_ids: plotGroup.additional_plot_ids,
    });

    navigate(
      // eslint-disable-next-line sonarjs/no-nested-template-literals
      `${pathSet.plotGroupPlots}?plotGroupId=${group.id}${workstreamId ? `&workstreamId=${workstreamId}` : ''}`,
      {
        state: {
          previousPath: pathSet.groups,
        },
      },
    );
  };

  return (
    <>
      <Stack spacing={8} data-testid='plot-group-list' className='w-full overflow-hidden scrollbar-hidden'>
        <Stack className='flex flex-row items-center justify-between gap-2'>
          <span className='typography-overline'>
            {t('global.organization.plotGroup.groups.pagination.groups', {
              count: filteredPlotGroups.length,
            })}
          </span>
          <Stack className='flex h-11 flex-row gap-1 bg-neutral-black-4 border border-neutral-black-4 rounded-full justify-start px-3 py-1 items-center min-w-52'>
            <Icon icon='ri-search-line' className='text-md' />
            <TextInput
              placeholder={t('shared.searchBar.placeholder')}
              className='w-full typography-body1 border-none bg-transparent h-full focus:border-none focus:outline-none focus-visible:outline-none px-0 py-0'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <Button
                variant='text'
                className='text-text-secondary hover:text-text-primary px-1 py-1'
                onClick={() => setSearchQuery('')}
              >
                <Icon icon='ri-close-line' className='text-sm' />
              </Button>
            )}
          </Stack>
        </Stack>
        <GenericTable data={filteredPlotGroups} defaultColumns={defaultColumns} onRowClick={onRowClick} />
      </Stack>
      <DeleteGroupWithConfirmation group={groupForDeletion} onClose={() => setGroupForDeletion(null)} />
      {!isNullish(groupForEdit) && (
        <EditGroup
          organizationId={organizationId}
          plotGroup={parsePlotGroupBodyData(groupForEdit)}
          open={!isNullish(groupForEdit)}
          onOpenChange={(open) => setGroupForEdit(open ? groupForEdit : null)}
        />
      )}
      {!isNullish(groupForAccess) && (
        <EditLimitedAccessOrganization
          organizationId={organizationId}
          open={!isNullish(groupForAccess)}
          onOpenChange={(open) => setGroupForAccess(open ? groupForAccess : null)}
          plotGroups={[parsePlotGroupBodyData(groupForAccess)]}
        />
      )}
    </>
  );
};

export const PlotGroupsPage = () => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId();
  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();

  return (
    <SignedInPageLayout
      items={[]}
      withCustomContentContainer
      navBarProps={{
        title: t('navbar.groups'),
        showFilters: false,
        children: !isLimitedAccessUser && isOrganizationMember && (
          <Stack direction='row' className='justify-end gap-4'>
            <CreateLimitedAccessOrganization organizationId={organizationId} />
          </Stack>
        ),
      }}
    >
      <Container gutterWidth={{ sm: 4 }} className='gap-10 my-8' data-testid='plot-group-list-page'>
        <PlotGroupsTable />
      </Container>
    </SignedInPageLayout>
  );
};
